import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { InstallFlow } from './install/InstallFlow';
import { DeniedBanner } from './install/DeniedBanner';
import { AnnounceHome } from './announce/AnnounceHome';
import { Settings } from './settings/Settings';
import { GearIcon } from './icons';
import { signOutOnThisDevice } from '../push/signOutDevice';
import { useSession } from '../data/session';
import { SignIn } from './auth/SignIn';
import { JoinNeighborhood } from './membership/JoinNeighborhood';
import { JoinFamily } from './membership/JoinFamily';
import { InviteNeeded } from './membership/InviteNeeded';
import { deriveRoute, postSignInTarget } from './router/routeLogic';
import {
  savePendingPathFromLocation,
  peekPendingPath,
  clearPendingPath
} from '../data/membership';
import { PartnerInvite } from './membership/PartnerInvite';
import { HeadedOutList } from './headedOut/HeadedOutList';
import { installSwNavigateListener } from './router/swNavigate';

export function App() {
  const buildInfo = useMemo(
    () => `${__APP_VERSION__} • ${__COMMIT_HASH__}`,
    []
  );

  const session = useSession();
  const [path, setPath] = useState<string>(typeof window !== 'undefined' ? window.location.pathname : '/');
  const [infoMsg, setInfoMsg] = useState<string | null>(null);
  const [justJoined, setJustJoined] = useState<boolean>(false);

  const navigate = useCallback(
    (to: string) => {
      if (to === path) return;
      window.history.pushState({}, '', to);
      setPath(to);
    },
    [path]
  );

  useEffect(() => {
    function onPop() {
      setPath(window.location.pathname);
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    // If signed out and on a join link, remember it for after sign-in
    if (!session.isSignedIn && (path.startsWith('/join/') || path.startsWith('/family/'))) {
      savePendingPathFromLocation(window.location);
    }
  }, [session.isSignedIn, path]);

  useEffect(() => {
    // If user is already in a family and opens a join/family link, send home with a note
    if (session.isSignedIn && session.familyId && (path.startsWith('/join/') || path.startsWith('/family/'))) {
      if (justJoined) return; // suppress redirect right after a successful join
      setInfoMsg('You’re already in a family.');
      navigate('/');
    }
  }, [session.isSignedIn, session.familyId, path, navigate, justJoined]);
  // Listen for SW navigate messages so we can switch routes without full reload
  useEffect(() => installSwNavigateListener(navigate), [navigate]);

  const route = deriveRoute(path, session);

  return (
    <div class="container">
      <header class="header">
        <h1 class="title">Shores Park Club</h1>
        {session.isSignedIn && session.familyId && (
          <button class="btn-plain" type="button" aria-label="Settings" onClick={() => navigate('/settings')} style="min-height:44px;">
            <GearIcon size={22} />
          </button>
        )}
        {session.isSignedIn && !session.familyId && (
          <button
            class="btn-plain"
            type="button"
            style="min-height:44px;"
            onClick={async () => {
              await signOutOnThisDevice();
              navigate('/sign-in');
            }}
          >
            Sign out
          </button>
        )}
      </header>

      <main class="main">
        {session.isLoading && null}
        {infoMsg && <div class="tiny-banner" role="status">{infoMsg}</div>}
        {route.kind === 'sign_in' && (
          <SignIn
            onSignedIn={() => {
              const pending = peekPendingPath();
              const target = postSignInTarget(path, pending);
              if (target) navigate(target);
            }}
          />
        )}
        {route.kind === 'join_neighborhood' && (
          <JoinNeighborhood
            token={route.token}
            onJoined={async () => {
              setJustJoined(true);
              await session.refresh();
              clearPendingPath();
              navigate('/invite-partner');
              setJustJoined(false);
            }}
          />
        )}
        {route.kind === 'join_family' && (
          <JoinFamily
            token={route.token}
            onJoined={async () => {
              setJustJoined(true);
              await session.refresh();
              clearPendingPath();
              navigate('/');
              setJustJoined(false);
            }}
          />
        )}
        {route.kind === 'invite_needed' && (
          <InviteNeeded
            showSignInButton={!session.isSignedIn}
            onSignInClick={() => navigate('/sign-in')}
          />
        )}
        {route.kind === 'invite_partner' && <PartnerInvite onDone={() => navigate('/')} />}
        {route.kind === 'settings' && <Settings navigate={navigate} />}
        {(route.kind === 'home' || route.kind === 'headed_out_list') && (
          <>
            <DeniedBanner />
            <InstallFlow />
            <AnnounceHome />
            {session.isSignedIn && session.familyId && (
              <div style="margin-top:12px;">
                <button class="btn-plain" type="button" onClick={() => navigate('/invite-partner')}>
                  Add your partner
                </button>
              </div>
            )}
            <HeadedOutList autoScrollIntoView={route.kind === 'headed_out_list'} />
          </>
        )}
      </main>

      <footer class="footer">
        <span>Build {buildInfo}</span>
      </footer>
    </div>
  );
}
